import { clearApiTestDraft } from './apiTestDraft.ts'
import { getAuthToken } from './tokenStorage.ts'

type StorageReader = Pick<Storage, 'getItem'>

/**
 * Tarayıcıda saklanan operasyonel verileri aktif üyeliğe bağlar.
 * Aynı kullanıcı farklı organizasyona geçebildiği için yalnız `sub`
 * yeterli değildir; backend'in JWT'ye koyduğu membership kimliği önceliklidir.
 */
export function getTenantStorageScope(storage: StorageReader = localStorage): string {
  try {
    // Cache data lives in localStorage even when authentication is scoped to
    // this tab. Resolve the same token as HTTP requests for browser storage;
    // injected storage readers retain their own identity for isolated tests.
    const token = typeof window !== 'undefined' && storage === window.localStorage
      ? getAuthToken()
      : storage.getItem('token') || ''
    const raw = token.split('.')[1]
    if (!raw) return 'anonymous'
    const segment = raw.replace(/-/g, '+').replace(/_/g, '/')
    const payload = JSON.parse(atob(segment.padEnd(Math.ceil(segment.length / 4) * 4, '=')))
    return String(payload.membership_id || payload.org_id || payload.sub || 'anonymous')
  } catch {
    return 'anonymous'
  }
}

export function tenantStorageKey(base: string, storage: StorageReader = localStorage): string {
  return `${base}:${getTenantStorageScope(storage)}`
}

/**
 * Önceki sürümler bu alanları hesap ayrımı olmadan yazıyordu. Bunlar
 * terminal logu, SSH hedefi veya API anahtarı içerebildiğinden yeni alana
 * taşımak yerine bir kez silmek güvenli olan davranıştır.
 */
export function clearLegacySensitiveTenantStorage(storage: Storage = localStorage): void {
  [
    'scpPersistentJobs',
    'scpPersistentJobsExpanded',
    'scpTerminalPrefs',
    'apitest_history',
    'apitest_active_env',
    'scpDnsZone',
  ].forEach(key => storage.removeItem(key))
}

/** Açık çıkışta ortak cihazda operasyonel veri bırakma. */
export function clearCurrentTenantSensitiveStorage(storage: Storage = localStorage): void {
  const scope = getTenantStorageScope(storage)
  clearApiTestDraft(`apitest_draft:${scope}`)
  ;[
    'scpPersistentJobs',
    'scpPersistentJobsExpanded',
    'scpTerminalPrefs',
    'apitest_history',
    'apitest_active_env',
    'scpDnsZone',
    'servernoxOfflineServerInventory',
    'servernoxOfflineAuthContext',
  ].forEach(base => storage.removeItem(`${base}:${scope}`))
  const aiPrefix = `servernox:ai-tabs:${scope}:`
  for (let index = storage.length - 1; index >= 0; index -= 1) {
    const key = storage.key(index)
    if (key?.startsWith(aiPrefix) || key?.startsWith(`servernox:ai-quota:${scope}:`) || (key?.startsWith('servernoxOfflineDashboard:') && key.endsWith(`:${scope}`))) storage.removeItem(key)
  }
}
